import axios from 'axios';
import { create } from 'zustand';

import type { AuthUser } from '../types/auth';

const STORAGE_KEY = 'art-of-thinking::auth';

const isBrowser = typeof window !== 'undefined';

const applyAuthHeader = (token: string | null): void => {
    if (token) {
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
        delete axios.defaults.headers.common.Authorization;
    }
};

/**
 * Decode JWT without verification (client-side only, for checking expiration).
 * JWT format: header.payload.signature
 */
const decodeJWT = (token: string): { exp?: number; [key: string]: unknown } | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        // Decode payload (second part)
        const payload = JSON.parse(atob(parts[1])) as Record<string, unknown>;
        return payload;
    } catch (error) {
        console.warn('Failed to decode JWT', error);
        return null;
    }
};

/**
 * Check if JWT token has expired by examining the exp claim.
 * Returns true if token is expired or invalid.
 */
const isTokenExpired = (token: string | null): boolean => {
    if (!token) {
        return true;
    }
    const payload = decodeJWT(token);
    if (!payload || typeof payload.exp !== 'number') {
        console.warn('Token missing or invalid exp claim');
        return true;
    }
    // exp is in seconds, Date.now() is in milliseconds
    const expirationTime = payload.exp * 1000;
    const isExpired = Date.now() >= expirationTime;
    if (isExpired) {
        console.warn('JWT token has expired');
    }
    return isExpired;
};

const readStoredAuth = (): { token: string | null; user: AuthUser | null } => {
    if (!isBrowser) {
        return { token: null, user: null };
    }
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return { token: null, user: null };
        }
        const parsed = JSON.parse(raw) as { token?: string; user?: AuthUser };
        const storedToken = parsed.token ?? null;
        
        // Validate token expiration
        if (storedToken && isTokenExpired(storedToken)) {
            console.warn('[Auth] Stored token has expired - clearing auth state');
            // Clear expired token immediately
            window.localStorage.removeItem(STORAGE_KEY);
            return { token: null, user: null };
        }
        
        return {
            token: storedToken,
            user: parsed.user ?? null,
        };
    } catch (error) {
        console.warn('Failed to read auth state from storage', error);
        return { token: null, user: null };
    }
};

/**
 * Old reminder links carried a login token in the address (?token=). They no longer sign anyone
 * in (#1312 phase 8: a token in a link signs in whoever sees it); the parameter is only removed
 * from the address. Reminder emails now carry a one-time link to /signin?link=...
 */
const dropUrlToken = (): void => {
    if (!isBrowser) return;
    try {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('token')) return;
        params.delete('token');
        const newUrl = params.toString()
            ? `${window.location.pathname}?${params.toString()}${window.location.hash}`
            : `${window.location.pathname}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);
    } catch {
        // Non-critical.
    }
};

dropUrlToken();
const initialAuth = readStoredAuth();
applyAuthHeader(initialAuth.token);

/**
 * Set up axios interceptor to handle 401 responses globally.
 * When a token expires, the interceptor will:
 * 1. Clear the auth state
 * 2. Redirect to login page
 * 3. Show a message to the user
 *
 * Exception: Public summary endpoints handle 401 gracefully in the component
 * (not auto-redirect), so they're excluded from this interceptor.
 */
const setupAxiosInterceptor = (): void => {
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            // Check if this is a 401 Unauthorized error
            if (error.response?.status === 401) {
                // Skip auto-redirect for certain endpoints that handle 401 gracefully
                const requestUrl = error.config?.url || '';
                const isPublicSummaryEndpoint = requestUrl.includes('/api/sessions/public/') && requestUrl.includes('/summary');
                const isFeedbackEndpoint = requestUrl.includes('/api/feedback');
                
                if (!isPublicSummaryEndpoint && !isFeedbackEndpoint) {
                    // Clear auth state from the store
                    const { clearAuth } = useAuthStore.getState();
                    clearAuth();

                    // Redirect to home page (landing/login)
                    if (isBrowser) {
                        const current = `${window.location.pathname}${window.location.search}`;
                        const returnTo = current.startsWith('/') && !current.startsWith('//')
                            ? `&returnTo=${encodeURIComponent(current)}`
                            : '';
                        window.location.href = `/signin?session=expired${returnTo}`;
                    }
                }
            }

            return Promise.reject(error);
        },
    );
};

export type AuthState = {
    token: string | null;
    user: AuthUser | null;
    setAuth: (payload: { token: string; user: AuthUser }) => void;
    clearAuth: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
    token: initialAuth.token,
    user: initialAuth.user,
    setAuth: ({ token, user }) => {
        applyAuthHeader(token);
        if (isBrowser) {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
        }
        set({ token, user });
    },
    clearAuth: () => {
        applyAuthHeader(null);
        if (isBrowser) {
            window.localStorage.removeItem(STORAGE_KEY);
        }
        set({ token: null, user: null });
    },
}));

// Initialize axios interceptor when store is created
if (isBrowser) {
    setupAxiosInterceptor();
}
